import React, { useEffect, useMemo, useState } from 'react';
import ssomaService from '../services/ssomaService';
import empleadoService from '../services/empleadoService';
import { textoEdad } from '../utils/edadDesdeFechaNacimiento';

function fmtFecha(fecha) {
  if (!fecha) return '—';
  const texto = String(fecha).split('T')[0];
  const [y, m, d] = texto.split('-');
  return y && m && d ? `${d}/${m}/${y}` : texto;
}

function mostrarValor(valor) {
  if (valor === null || valor === undefined || String(valor).trim() === '') return '—';
  return valor;
}

function labelSexo(sexo) {
  if (!sexo) return '—';
  const clave = String(sexo).toLowerCase();
  if (clave === 'm' || clave === 'masculino') return 'Masculino';
  if (clave === 'f' || clave === 'femenino') return 'Femenino';
  return sexo;
}

function labelEstadoCivil(estado) {
  if (!estado) return '—';
  const map = {
    soltero: 'Soltero(a)',
    casado: 'Casado(a)',
    conviviente: 'Conviviente',
    divorciado: 'Divorciado(a)',
    viudo: 'Viudo(a)',
  };
  const clave = String(estado).toLowerCase();
  return map[clave] || estado;
}

const CampoLabel = ({ etiqueta, valor, anchoCompleto = false }) => (
  <div className={anchoCompleto ? 'sm:col-span-2' : ''}>
    <dt className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">{etiqueta}</dt>
    <dd className="mt-1 text-sm text-gray-900 whitespace-pre-wrap break-words">{mostrarValor(valor)}</dd>
  </div>
);

const GrillaLabels = ({ items }) => (
  <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-4">
    {items.map((item) => (
      <CampoLabel key={item.etiqueta} etiqueta={item.etiqueta} valor={item.valor} anchoCompleto={item.anchoCompleto} />
    ))}
  </dl>
);

const Bloque = ({ titulo, children }) => (
  <section className="border border-gray-200 rounded-xl p-4 bg-gray-50/50">
    <h4 className="text-sm font-semibold text-gray-900 mb-3">{titulo}</h4>
    {children}
  </section>
);

function itemsDatosPersonales(empleado) {
  if (!empleado) return [];
  return [
    { etiqueta: 'Apellidos y nombres', valor: empleado.nombre_completo, anchoCompleto: true },
    { etiqueta: 'Tipo de documento', valor: empleado.tipo_documento },
    { etiqueta: 'N.º documento', valor: empleado.numero_documento },
    { etiqueta: 'Fecha de nacimiento', valor: fmtFecha(empleado.fecha_nacimiento) },
    { etiqueta: 'Edad', valor: textoEdad(empleado.fecha_nacimiento) },
    { etiqueta: 'Sexo', valor: labelSexo(empleado.sexo) },
    { etiqueta: 'Estado civil', valor: labelEstadoCivil(empleado.estado_civil) },
    { etiqueta: 'N.º de hijos', valor: empleado.numero_hijos },
    { etiqueta: 'Estado en la empresa', valor: empleado.estado },
  ];
}

function itemsContacto(empleado) {
  if (!empleado) return [];
  return [
    { etiqueta: 'Teléfono', valor: empleado.telefono },
    { etiqueta: 'Correo electrónico', valor: empleado.email },
    { etiqueta: 'Dirección', valor: empleado.direccion, anchoCompleto: true },
    { etiqueta: 'Distrito', valor: empleado.distrito },
    { etiqueta: 'Provincia', valor: empleado.provincia },
    { etiqueta: 'Departamento', valor: empleado.departamento },
    { etiqueta: 'Contacto de emergencia', valor: empleado.contacto_emergencia },
    { etiqueta: 'Teléfono de emergencia', valor: empleado.telefono_emergencia },
  ];
}

function itemsLaborales(empleado) {
  if (!empleado) return [];
  return [
    { etiqueta: 'Cargo', valor: empleado.cargo },
    { etiqueta: 'Categoría', valor: empleado.categoria },
    { etiqueta: 'Proyecto', valor: empleado.proyecto },
    { etiqueta: 'Área de trabajo', valor: empleado.areas_de_trabajo },
    { etiqueta: 'Tipo de contratación', valor: empleado.tipo_contratacion },
    { etiqueta: 'Tipo de planilla', valor: empleado.tipo_planilla },
    { etiqueta: 'Fecha de ingreso', valor: fmtFecha(empleado.fecha_ingreso) },
    { etiqueta: 'Régimen laboral', valor: empleado.regimen_laboral },
    { etiqueta: 'Fecha cómputo RH', valor: empleado.fecha_computo_rh },
    { etiqueta: 'Hora de entrada', valor: empleado.hora_entrada },
    { etiqueta: 'Hora de salida', valor: empleado.hora_salida },
    { etiqueta: 'Observaciones (RRHH)', valor: empleado.observaciones, anchoCompleto: true },
  ];
}

function itemsPensiones(empleado) {
  if (!empleado) return [];
  return [
    { etiqueta: 'Sistema pensionario', valor: empleado.sistema_pensionario },
    { etiqueta: 'AFP', valor: empleado.afp },
    { etiqueta: 'Tipo AFP', valor: empleado.tipo_afp },
    { etiqueta: 'CUSPP', valor: empleado.cuspp },
    { etiqueta: 'Forma de pago', valor: empleado.forma_pago },
  ];
}

const SsomaMiFichaRegistroModal = ({
  open,
  onClose,
  empresaId,
  fotosPersonalActivo = false,
  empleadoId = null,
  soloPropio = false,
  titulo = null,
}) => {
  const [detalle, setDetalle] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !empresaId) return;
    let cancelado = false;
    const cargar = async () => {
      setCargando(true);
      setError('');
      setDetalle(null);
      const resp =
        soloPropio || !empleadoId
          ? await ssomaService.miFichaRegistro(empresaId)
          : await ssomaService.ficha(empresaId, empleadoId);
      if (cancelado) return;
      if (!resp.success) {
        setError(resp.error || 'No se pudo cargar la ficha de registro');
      } else {
        setDetalle(resp.data);
      }
      setCargando(false);
    };
    cargar();
    return () => {
      cancelado = true;
    };
  }, [open, empresaId, empleadoId, soloPropio]);

  const empleado = detalle?.empleado;
  const empleadoIdVista = empleado?.id;

  const bloquesEmpleado = useMemo(() => {
    if (!empleado) return null;
    return {
      personales: itemsDatosPersonales(empleado),
      contacto: itemsContacto(empleado),
      laborales: itemsLaborales(empleado),
      pensiones: itemsPensiones(empleado),
    };
  }, [empleado]);

  if (!open) return null;

  const tituloModal =
    titulo || (soloPropio || !empleadoId ? 'Mi ficha de registro (RRHH)' : 'Ficha de registro (RRHH)');

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="mi-ficha-ssoma-titulo"
    >
      <div
        className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-white rounded-2xl shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 flex items-start justify-between gap-3 px-6 py-4 border-b border-gray-200 bg-white rounded-t-2xl">
          <div className="flex items-start gap-3 min-w-0">
            {fotosPersonalActivo && empleado?.foto_carnet_url && (
              <img
                src={empleadoService.urlFotoCarnet(empleado.foto_carnet_url)}
                alt=""
                className="h-14 w-14 rounded-full object-cover shrink-0 border border-gray-200"
              />
            )}
            <div className="min-w-0">
              <h2 id="mi-ficha-ssoma-titulo" className="text-lg font-semibold text-gray-900">
                {tituloModal}
              </h2>
              {empleado ? (
                <p className="text-sm text-gray-600 mt-0.5">Consulta en solo lectura</p>
              ) : (
                <p className="text-sm text-gray-500">Datos de RRHH del trabajador.</p>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-100"
          >
            Cerrar
          </button>
        </div>

        <div className="p-6 space-y-5">
          {cargando && <p className="text-sm text-gray-500">Cargando ficha de registro…</p>}
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</div>
          )}
          {!cargando && !error && detalle && empleadoIdVista && bloquesEmpleado && (
            <>
              <p className="text-sm text-teal-800 bg-teal-50 border border-teal-100 rounded-lg px-4 py-3">
                {soloPropio || !empleadoId
                  ? 'Aquí ves tus datos de RRHH. Tallas, salud, SCTR y el resto se gestionan en la ficha SSOMA del módulo.'
                  : 'Datos de RRHH del trabajador (solo lectura). El resto de la ficha SSOMA está en el panel lateral.'}
              </p>

              <Bloque titulo="Datos personales">
                <GrillaLabels items={bloquesEmpleado.personales} />
              </Bloque>

              <Bloque titulo="Contacto y domicilio">
                <GrillaLabels items={bloquesEmpleado.contacto} />
              </Bloque>

              <Bloque titulo="Datos laborales">
                <GrillaLabels items={bloquesEmpleado.laborales} />
              </Bloque>

              <Bloque titulo="Pensiones y forma de pago">
                <GrillaLabels items={bloquesEmpleado.pensiones} />
              </Bloque>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SsomaMiFichaRegistroModal;
